/**
 * @file DeliveryPackageCard.tsx
 * @description Одно место (коробка) в форме расчёта доставки (phase19.12 шаг 5).
 *
 * Вес, габариты, комментарий и раскладка позиций заказа. Позиция может лежать
 * ровно в одном месте (решение пользователя: дробление запрещено) — занятые
 * другими местами позиции показываем выключенными с подписью «в месте N».
 *
 * Вес и коробка считаются по товарам: автоматически, пока менеджер не правил
 * поля руками, и по кнопке «Рассчитать по товарам» — всегда.
 */

import { Trash2 } from "lucide-react";
import { useEffect, useMemo } from "react";

import { DeliveryPackageItemRow } from "./DeliveryPackageItemRow";
import { DeliveryPackageSuggestion } from "./DeliveryPackageSuggestion";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import type { OrderDeliveryLineDto } from "../../lib/api-orders";
import type { DeliveryPackageDraft } from "../../lib/delivery-quote";
import {
  autoFillPatch,
  suggestPackageDims,
  suggestionToDraftPatch,
} from "../../lib/package-suggestion";

interface Props {
  index: number;
  value: DeliveryPackageDraft;
  onChange: (patch: Partial<DeliveryPackageDraft>) => void;
  /** Не задан — место удалять нельзя (первое место всегда остаётся). */
  onRemove?: () => void;
  /** Позиции заказа; пусто — справочный расчёт без раскладки. */
  items?: OrderDeliveryLineDto[];
  /** orderItemId → номер места, где позиция уже лежит. */
  placedElsewhere?: Map<string, number>;
  /** Вес, посчитанный сервером по позициям (последний расчёт). */
  autoWeightG?: number | null;
  disabled?: boolean;
}

const DIMS = [
  { field: "lengthMm", label: "Длина, мм" },
  { field: "widthMm", label: "Ширина, мм" },
  { field: "heightMm", label: "Высота, мм" },
] as const;

export function DeliveryPackageCard({
  index,
  value,
  onChange,
  onRemove,
  items,
  placedElsewhere,
  autoWeightG,
  disabled,
}: Props) {
  const assigned = useMemo(
    () => (items ?? []).filter((i) => value.orderItemIds.includes(i.orderItemId)),
    [items, value.orderItemIds],
  );
  const suggestion = useMemo(() => suggestPackageDims(assigned), [assigned]);
  const fromItems = !value.touched && assigned.length > 0;

  // Раскладку поменяли — пересчитываем место, пока его не правили руками.
  useEffect(() => {
    if (value.touched || assigned.length === 0) return;
    const patch = autoFillPatch(suggestion, value);
    if (patch) onChange(patch);
  }, [suggestion, value, assigned.length, onChange]);

  const toggleItem = (orderItemId: string, checked: boolean): void => {
    onChange({
      orderItemIds: checked
        ? [...value.orderItemIds, orderItemId]
        : value.orderItemIds.filter((id) => id !== orderItemId),
    });
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg p-3 [box-shadow:inset_0_0_0_1px_var(--outline-variant)]">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">Место {index + 1}</span>
          {fromItems ? <Badge variant="secondary">из позиций</Badge> : null}
          {value.touched ? <Badge variant="warning">правка вручную</Badge> : null}
        </div>
        {onRemove ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Удалить место ${index + 1}`}
            onClick={onRemove}
            disabled={disabled}
          >
            <Trash2 className="size-4" />
          </Button>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`delivery-quote-weight-${index}`}>Вес, г</Label>
          <Input
            id={`delivery-quote-weight-${index}`}
            inputMode="numeric"
            placeholder={autoWeightG ? String(autoWeightG) : "например, 1200"}
            value={value.weightG}
            disabled={disabled}
            onChange={(e) => onChange({ weightG: e.target.value, touched: true })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`delivery-quote-comment-${index}`}>Комментарий</Label>
          <Input
            id={`delivery-quote-comment-${index}`}
            placeholder="например, хрупкое"
            value={value.comment}
            disabled={disabled}
            onChange={(e) => onChange({ comment: e.target.value })}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {DIMS.map(({ field, label }) => (
          <div key={field} className="flex flex-col gap-1.5">
            <Label htmlFor={`delivery-quote-${field}-${index}`}>{label}</Label>
            <Input
              id={`delivery-quote-${field}-${index}`}
              inputMode="numeric"
              value={value[field]}
              disabled={disabled}
              onChange={(e) => onChange({ [field]: e.target.value, touched: true })}
            />
          </div>
        ))}
      </div>

      {assigned.length > 0 ? (
        <DeliveryPackageSuggestion
          suggestion={suggestion}
          disabled={disabled}
          onApply={() => onChange(suggestionToDraftPatch(suggestion))}
        />
      ) : (
        <p className="text-xs text-on-surface-variant">
          Габариты пустые — возьмём коробку по умолчанию из настроек перевозчика.
        </p>
      )}

      {items?.length ? (
        <div className="flex flex-col gap-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
            Что лежит в месте
          </div>
          {items.map((item) => (
            <DeliveryPackageItemRow
              key={item.orderItemId}
              item={item}
              checked={value.orderItemIds.includes(item.orderItemId)}
              placedIn={placedElsewhere?.get(item.orderItemId)}
              disabled={disabled}
              onToggle={(checked) => toggleItem(item.orderItemId, checked)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
